import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from '@/stores/auth'

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/',
      name: 'home',
      component: () => import('@/components/HomeView.vue'),
    },
    {
      path: '/dashboard',
      name: 'dashboard',
      component: () => import('@/components/DashboardView.vue'),
    },
    {
      path: '/tasks',
      name: 'tasks',
      component: () => import('@/components/TasksView.vue'),
    },
    {
      path: '/tasks/new',
      name: 'task-new',
      component: () => import('@/components/TaskDetailView.vue'),
    },
    {
      path: '/tasks/:id',
      name: 'task-detail',
      component: () => import('@/components/TaskDetailView.vue'),
    },
    {
      path: '/people',
      name: 'people',
      component: () => import('@/components/PeopleView.vue'),
    },
    {
      path: '/people/:id/commits',
      name: 'person-commits',
      component: () => import('@/components/PersonCommitStatsView.vue'),
      props: true,
    },
    {
      path: '/tags',
      name: 'tags',
      component: () => import('@/components/TagsView.vue'),
    },
    {
      path: '/projects',
      name: 'projects',
      component: () => import('@/components/ProjectsView.vue'),
    },
    {
      path: '/projects/:slug',
      name: 'project-detail',
      component: () => import('@/components/ProjectDetailView.vue'),
      props: true,
    },
    {
      path: '/knowledge',
      name: 'knowledge',
      component: () => import('@/components/KnowledgeView.vue'),
    },
    {
      path: '/search',
      name: 'search',
      component: () => import('@/components/SearchView.vue'),
    },
    {
      // «Мои отчёты» — для любого сотрудника (editor+): персону бэкенд берёт из сессии
      path: '/my-reports',
      name: 'my-reports',
      component: () => import('@/components/reports/MyReportsView.vue'),
      meta: { minRole: 'editor' },
    },
    {
      path: '/reports',
      name: 'reports',
      component: () => import('@/components/ReportView.vue'),
      meta: { minRole: 'executive' },
    },
    {
      path: '/reports/tag-time',
      name: 'tag-time-report',
      component: () => import('@/components/reports/TagTimeReportView.vue'),
      meta: { minRole: 'executive' },
    },
    {
      path: '/reports/flow',
      name: 'flow-metrics',
      component: () => import('@/components/reports/FlowMetricsView.vue'),
      meta: { minRole: 'executive' },
    },
    {
      path: '/reports/timesheet',
      name: 'timesheet',
      component: () => import('@/components/timesheet/TimesheetView.vue'),
      meta: { minRole: 'executive' },
    },
    {
      path: '/reports/timesheet/:personId',
      name: 'timesheet-detail',
      component: () => import('@/components/timesheet/TimesheetDetailView.vue'),
      props: true,
      meta: { minRole: 'executive' },
    },
    {
      path: '/planning',
      name: 'planning',
      component: () => import('@/components/planning/PlanGanttView.vue'),
      meta: { minRole: 'editor' },
    },
    {
      path: '/analytics',
      name: 'analytics',
      component: () => import('@/components/analytics/AnalyticsView.vue'),
      meta: { minRole: 'executive' },
    },
    {
      path: '/time-tracking',
      name: 'time-tracking',
      component: () => import('@/components/TimeTrackingView.vue'),
    },
    {
      path: '/time-tracking/admin',
      name: 'time-tracking-admin',
      component: () => import('@/components/TimeTrackingAdminView.vue'),
      meta: { requiresTimeTrackingAdmin: true },
    },
    {
      path: '/ideas',
      name: 'ideas',
      component: () => import('@/components/IdeasView.vue'),
    },
    {
      path: '/ai',
      name: 'ai',
      component: () => import('@/components/AiAssistantView.vue'),
    },
    {
      path: '/settings',
      name: 'settings',
      component: () => import('@/components/SettingsView.vue'),
    },
    {
      path: '/admin',
      name: 'admin',
      component: () => import('@/components/AdminView.vue'),
    },
    {
      path: '/admin/work-calendar',
      name: 'work-calendar',
      component: () => import('@/components/admin/WorkCalendarView.vue'),
      meta: { requiresSuperAdmin: true },
    },
    {
      path: '/admin/task-groomer',
      name: 'task-groomer',
      component: () => import('@/components/admin/TaskGroomerView.vue'),
      meta: { requiresSuperAdmin: true },
    },
    {
      path: '/admin/commit-authors',
      name: 'commit-authors',
      component: () => import('@/components/admin/CommitAuthorsView.vue'),
      meta: { minRole: 'admin' },
    },
    {
      path: '/technical',
      name: 'technical',
      component: () => import('@/components/TechnicalDashboard.vue'),
    },
    {
      path: '/digital-maturity',
      name: 'digital-maturity',
      component: () => import('@/components/DigitalMaturityView.vue'),
      meta: { requiresStrategy: true },
    },
    // Strategy section (unified: replaces executive + digitalization)
    {
      path: '/strategy',
      component: () => import('@/views/StrategyView.vue'),
      meta: { requiresStrategy: true },
      children: [
        {
          path: '',
          name: 'strategy',
          redirect: '/strategy/overview',
        },
        {
          path: 'overview',
          name: 'strategy-overview',
          component: () => import('@/components/strategy/StrategicDashboard.vue'),
          meta: { strategyMinRole: 'executive' },
        },
        {
          path: 'directions',
          name: 'strategy-directions',
          component: () => import('@/components/strategy/ManagementDashboard.vue'),
          meta: { strategyMinRole: 'admin' },
        },
        {
          path: 'directions/:id',
          name: 'strategy-direction',
          component: () => import('@/components/strategy/DirectionDetail.vue'),
          props: true,
          meta: { strategyMinRole: 'viewer' },
        },
        {
          path: 'measures/:id',
          name: 'strategy-measure',
          component: () => import('@/components/strategy/MeasureDetail.vue'),
          props: true,
          meta: { strategyMinRole: 'viewer' },
        },
        {
          path: 'operations',
          name: 'strategy-operations',
          component: () => import('@/components/strategy/OperationalDashboard.vue'),
          meta: { strategyMinRole: 'editor' },
        },
        {
          path: 'my',
          name: 'strategy-my',
          component: () => import('@/components/strategy/FunctionalDashboard.vue'),
        },
        {
          path: 'classification',
          name: 'strategy-classification',
          component: () => import('@/components/strategy/ClassificationQueue.vue'),
          meta: { strategyMinRole: 'executive' },
        },
      ],
    },
    // Legacy redirects
    { path: '/executive', redirect: '/strategy/overview' },
    { path: '/digitalization', redirect: '/strategy/directions' },
    { path: '/digitalization/dashboard', redirect: '/strategy/directions' },
    { path: '/digitalization/directions', redirect: '/strategy/directions' },
    { path: '/digitalization/directions/:id', redirect: to => `/strategy/directions/${to.params.id}` },
    // Public routes (no auth required)
    {
      path: '/public/submit/:projectSlug',
      name: 'public-submit',
      component: () => import('@/views/PublicSubmitView.vue'),
      meta: { public: true },
    },
  ],
})

// Navigation guard for role-based access
router.beforeEach(async (to, _from, next) => {
  // Public routes bypass all auth checks
  if (to.meta.public) {
    return next()
  }

  // Get auth store (must be inside guard to ensure pinia is ready)
  const authStore = useAuthStore()

  // Wait for auth to initialize if user is not loaded yet
  if (!authStore.user && authStore.isAuthenticated === false) {
    // User state not yet determined, allow navigation
    // App.vue will handle init and redirect if needed
  }

  // Check if user has no app roles - only allow settings page
  if (authStore.user && !authStore.hasAppAccess) {
    if (to.name !== 'settings') {
      console.warn('User has no app roles, redirecting to settings')
      return next({ name: 'settings' })
    }
  }

  // Стратегический слой — только по роли strategy (меню скрыто тем же условием)
  if (to.matched.some(r => r.meta.requiresStrategy) && authStore.user && !authStore.hasStrategyAccess) {
    return next({ name: 'home' })
  }

  // Проверка доступа по минимальной роли.
  // strategyMinRole — исторический ключ модуля стратегии; minRole — тот же
  // механизм для остальных разделов (отчёты, аналитика).
  const minRole = (to.meta.minRole ?? to.meta.strategyMinRole) as string | undefined
  if (minRole) {
    const roleHierarchy: Record<string, number> = {
      superadmin: 100, admin: 80, executive: 70, editor: 50, viewer: 20,
    }
    const requiredLevel = roleHierarchy[minRole] ?? 0
    const userRoles = authStore.user?.roles || []
    const maxUserLevel = Math.max(0, ...userRoles.map(r => roleHierarchy[r] ?? 0))
    if (maxUserLevel < requiredLevel) {
      console.warn(`Access denied: requires ${minRole} role`)
      return next({ name: 'home' })
    }
  }

  // Check superadmin access
  if (to.meta.requiresSuperAdmin) {
    if (!authStore.isSuperAdmin) {
      console.warn('Access denied: superadmin required')
      return next({ name: 'home' })
    }
  }

  next()
})

export default router
